import {
  HeaderCrop,
  NormalizedPoint,
  SceneSize,
  SceneTransform,
  StickerPlacement,
  ViewportPoint,
} from "./types";

export const MIN_CROP_ZOOM = 1;
export const MAX_CROP_ZOOM = 4;
export const MIN_STICKER_SCALE = 0.2;
export const MAX_STICKER_SCALE = 2;
export const MIN_VISIBLE_STICKER_FRACTION = 0.05;
const FLOATING_POINT_TOLERANCE = 1e-12;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

const finiteOr = (value: number, fallback: number) =>
  Number.isFinite(value) ? value : fallback;

const assertPositiveSize = (size: SceneSize, name: string) => {
  if (
    !Number.isFinite(size.width) ||
    !Number.isFinite(size.height) ||
    size.width <= 0 ||
    size.height <= 0
  ) {
    throw new RangeError(`${name} dimensions must be positive finite numbers`);
  }
};

export const getCoverScale = (
  imageSize: SceneSize,
  viewportSize: SceneSize
) => {
  assertPositiveSize(imageSize, "Image");
  assertPositiveSize(viewportSize, "Viewport");

  return Math.max(
    viewportSize.width / imageSize.width,
    viewportSize.height / imageSize.height
  );
};

export const normalizeCrop = (
  crop: HeaderCrop,
  imageSize: SceneSize,
  viewportSize: SceneSize
): HeaderCrop => {
  const zoom = clamp(
    finiteOr(crop.zoom, MIN_CROP_ZOOM),
    MIN_CROP_ZOOM,
    MAX_CROP_ZOOM
  );
  const scale = getCoverScale(imageSize, viewportSize) * zoom;
  const halfVisibleWidth = Math.min(
    0.5,
    viewportSize.width / (2 * scale * imageSize.width)
  );
  const halfVisibleHeight = Math.min(
    0.5,
    viewportSize.height / (2 * scale * imageSize.height)
  );

  return {
    centerX: clamp(
      finiteOr(crop.centerX, 0.5),
      halfVisibleWidth,
      1 - halfVisibleWidth
    ),
    centerY: clamp(
      finiteOr(crop.centerY, 0.5),
      halfVisibleHeight,
      1 - halfVisibleHeight
    ),
    zoom,
  };
};

export const createSceneTransform = (
  crop: HeaderCrop,
  imageSize: SceneSize,
  viewportSize: SceneSize
): SceneTransform => {
  const normalizedCrop = normalizeCrop(crop, imageSize, viewportSize);
  const scale = getCoverScale(imageSize, viewportSize) * normalizedCrop.zoom;

  return {
    crop: normalizedCrop,
    scale,
    offsetX:
      viewportSize.width / 2 - normalizedCrop.centerX * imageSize.width * scale,
    offsetY:
      viewportSize.height / 2 -
      normalizedCrop.centerY * imageSize.height * scale,
  };
};

export const createUnconstrainedSceneTransform = (
  crop: HeaderCrop,
  imageSize: SceneSize,
  viewportSize: SceneSize
): SceneTransform => {
  const normalizedCrop = {
    centerX: finiteOr(crop.centerX, 0.5),
    centerY: finiteOr(crop.centerY, 0.5),
    zoom: clamp(
      finiteOr(crop.zoom, MIN_CROP_ZOOM),
      MIN_CROP_ZOOM,
      MAX_CROP_ZOOM
    ),
  };
  const scale = getCoverScale(imageSize, viewportSize) * normalizedCrop.zoom;

  return {
    crop: normalizedCrop,
    scale,
    offsetX:
      viewportSize.width / 2 - normalizedCrop.centerX * imageSize.width * scale,
    offsetY:
      viewportSize.height / 2 -
      normalizedCrop.centerY * imageSize.height * scale,
  };
};

export const panCrop = (
  crop: HeaderCrop,
  delta: ViewportPoint,
  imageSize: SceneSize,
  viewportSize: SceneSize
): HeaderCrop => {
  const transform = createSceneTransform(crop, imageSize, viewportSize);

  return normalizeCrop(
    {
      ...crop,
      centerX:
        transform.crop.centerX - delta.x / (imageSize.width * transform.scale),
      centerY:
        transform.crop.centerY - delta.y / (imageSize.height * transform.scale),
    },
    imageSize,
    viewportSize
  );
};

export const setCropZoom = (
  crop: HeaderCrop,
  zoom: number,
  imageSize: SceneSize,
  viewportSize: SceneSize
): HeaderCrop => normalizeCrop({ ...crop, zoom }, imageSize, viewportSize);

export interface CropGuideRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const MOBILE_CROP_VIEWPORT = { width: 1, height: 1 };
const CENTERED_CROP: HeaderCrop = { centerX: 0.5, centerY: 0.5, zoom: 1 };

export const createMobileCropGuide = (
  crop: HeaderCrop,
  imageSize: SceneSize,
  viewportSize: SceneSize,
  overviewCrop: HeaderCrop = CENTERED_CROP
): CropGuideRect => {
  const overview = createUnconstrainedSceneTransform(
    overviewCrop,
    imageSize,
    viewportSize
  );
  const normalizedCrop = normalizeCrop(crop, imageSize, MOBILE_CROP_VIEWPORT);
  const sourceSize =
    Math.min(imageSize.width, imageSize.height) / normalizedCrop.zoom;
  const guideSize = sourceSize * overview.scale;
  const center = imagePointToViewport(
    { x: normalizedCrop.centerX, y: normalizedCrop.centerY },
    imageSize,
    overview
  );

  return {
    left: center.x - guideSize / 2,
    top: center.y - guideSize / 2,
    width: guideSize,
    height: guideSize,
  };
};

export const mobileCropFromGuide = (
  guide: CropGuideRect,
  imageSize: SceneSize,
  viewportSize: SceneSize,
  overviewCrop: HeaderCrop = CENTERED_CROP
): HeaderCrop => {
  const overview = createUnconstrainedSceneTransform(
    overviewCrop,
    imageSize,
    viewportSize
  );
  const center = viewportPointToImage(
    {
      x: guide.left + guide.width / 2,
      y: guide.top + guide.height / 2,
    },
    imageSize,
    overview
  );
  const zoom =
    (Math.min(imageSize.width, imageSize.height) * overview.scale) /
    guide.width;

  return normalizeCrop(
    { centerX: center.x, centerY: center.y, zoom },
    imageSize,
    MOBILE_CROP_VIEWPORT
  );
};

export const panMobileOverviewCrop = (
  crop: HeaderCrop,
  delta: ViewportPoint,
  guide: CropGuideRect,
  imageSize: SceneSize,
  viewportSize: SceneSize
): HeaderCrop => {
  const transform = createUnconstrainedSceneTransform(
    crop,
    imageSize,
    viewportSize
  );
  const imageWidth = imageSize.width * transform.scale;
  const imageHeight = imageSize.height * transform.scale;
  const proposedCenterX = transform.crop.centerX - delta.x / imageWidth;
  const proposedCenterY = transform.crop.centerY - delta.y / imageHeight;
  const minimumCenterX = (viewportSize.width / 2 - guide.left) / imageWidth;
  const maximumCenterX =
    (viewportSize.width / 2 + imageWidth - guide.left - guide.width) /
    imageWidth;
  const minimumCenterY = (viewportSize.height / 2 - guide.top) / imageHeight;
  const maximumCenterY =
    (viewportSize.height / 2 + imageHeight - guide.top - guide.height) /
    imageHeight;

  return {
    centerX: clamp(proposedCenterX, minimumCenterX, maximumCenterX),
    centerY: clamp(proposedCenterY, minimumCenterY, maximumCenterY),
    zoom: transform.crop.zoom,
  };
};

export const imagePointToViewport = (
  point: NormalizedPoint,
  imageSize: SceneSize,
  transform: SceneTransform
): ViewportPoint => ({
  x: transform.offsetX + point.x * imageSize.width * transform.scale,
  y: transform.offsetY + point.y * imageSize.height * transform.scale,
});

export const viewportPointToImage = (
  point: ViewportPoint,
  imageSize: SceneSize,
  transform: SceneTransform
): NormalizedPoint => ({
  x: (point.x - transform.offsetX) / (imageSize.width * transform.scale),
  y: (point.y - transform.offsetY) / (imageSize.height * transform.scale),
});

export const getStickerWidthRange = (defaultWidth: number) => {
  if (!Number.isFinite(defaultWidth) || defaultWidth <= 0) {
    throw new RangeError("Default sticker width must be a positive number");
  }

  return {
    min: defaultWidth * MIN_STICKER_SCALE,
    max: defaultWidth * MAX_STICKER_SCALE,
  };
};

export const clampStickerWidth = (width: number, defaultWidth: number) => {
  const range = getStickerWidthRange(defaultWidth);
  return clamp(finiteOr(width, defaultWidth), range.min, range.max);
};

type PlacementRect = Pick<StickerPlacement, "x" | "y" | "width">;

export const getVisibleStickerFraction = (
  placement: PlacementRect,
  stickerSize: SceneSize,
  imageSize: SceneSize
) => {
  assertPositiveSize(stickerSize, "Sticker");
  assertPositiveSize(imageSize, "Image");

  if (!Number.isFinite(placement.width) || placement.width <= 0) {
    return 0;
  }

  const height =
    (placement.width * imageSize.width * stickerSize.height) /
    (stickerSize.width * imageSize.height);

  if (!Number.isFinite(placement.x) || !Number.isFinite(placement.y)) {
    return 0;
  }

  const intersectionWidth = Math.max(
    0,
    Math.min(1, placement.x + placement.width) - Math.max(0, placement.x)
  );
  const intersectionHeight = Math.max(
    0,
    Math.min(1, placement.y + height) - Math.max(0, placement.y)
  );

  return (intersectionWidth * intersectionHeight) / (placement.width * height);
};

export const hasMinimumVisibleStickerArea = (
  placement: PlacementRect,
  stickerSize: SceneSize,
  imageSize: SceneSize,
  minimumVisibleFraction = MIN_VISIBLE_STICKER_FRACTION
) => {
  const safeMinimum = clamp(
    finiteOr(minimumVisibleFraction, MIN_VISIBLE_STICKER_FRACTION),
    0,
    1
  );
  return (
    getVisibleStickerFraction(placement, stickerSize, imageSize) +
      FLOATING_POINT_TOLERANCE >=
    safeMinimum
  );
};
